import { useState } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import SiteReveal from "../shared/SiteReveal";
import { NAV, STATS, SERVICES, PROCESS, CASES, TEAM, CLIENTS } from "./nexusData";
import { BRAND as BASE_BRAND } from "./nexusData";
import { getDemoProfile } from "../shared/industryCatalog";
import "../shared/siteReveal.css";
import "./NexusSite.css";

function Modal({ message, onClose }) {
  return (
    <div className="nx-modal-overlay" onClick={onClose}>
      <div className="nx-modal" onClick={(e) => e.stopPropagation()}>
        <h3>문의가 접수되었습니다</h3>
        <p>{message}</p>
        <button type="button" className="nx-btn nx-btn--primary" onClick={onClose}>확인</button>
      </div>
    </div>
  );
}

export default function NexusSite({ embed = false, profileId = "nexus" }) {
  const profile = getDemoProfile("corporate", profileId);
  const BRAND = profile?.brand ?? BASE_BRAND;
  const theme = profile?.theme ?? {};
  const [menu, setMenu] = useState(false);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ company: "", name: "", email: "", type: "", message: "" });

  const scroll = (id) => {
    setMenu(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = (e) => {
    e.preventDefault();
    setModal(true);
    setForm({ company: "", name: "", email: "", type: "", message: "" });
  };

  return (
    <div
      className={`nx-site${embed ? " nx-site--embed" : ""}`}
      style={{ "--c-navy": theme.navy ?? "#1a365d" }}
    >
      <header className="nx-header">
        <div className="nx-header__inner">
          <a href="#nx-top" className="nx-brand" onClick={(e) => { e.preventDefault(); scroll("nx-top"); }}>
            <strong>{BRAND.name}</strong>
            <span>{BRAND.nameEn}</span>
          </a>
          <nav className="nx-nav">
            {NAV.map((n) => (
              <a key={n.id} href={`#${n.id}`} onClick={(e) => { e.preventDefault(); scroll(n.id); }}>{n.label}</a>
            ))}
          </nav>
          <button type="button" className="nx-btn nx-btn--primary nx-header__cta" onClick={() => scroll("contact")}>
            프로젝트 문의
          </button>
          <button type="button" className="nx-menu" onClick={() => setMenu((v) => !v)} aria-label="메뉴">
            {menu ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
        {menu && (
          <nav className="nx-mobile">
            {NAV.map((n) => (
              <a key={n.id} href={`#${n.id}`} onClick={(e) => { e.preventDefault(); scroll(n.id); }}>{n.label}</a>
            ))}
          </nav>
        )}
      </header>

      <section id="nx-top" className="nx-hero">
        <div className="nx-wrap nx-hero__grid">
          <div>
            <span className="nx-eyebrow">{BRAND.nameEn}</span>
            <h1>{BRAND.slogan}</h1>
            <p>{BRAND.intro}</p>
            <div className="nx-hero__btns">
              <button type="button" className="nx-btn nx-btn--primary" onClick={() => scroll("contact")}>상담 신청</button>
              <button type="button" className="nx-btn nx-btn--outline" onClick={() => scroll("services")}>서비스 보기</button>
            </div>
          </div>
          <div className="nx-hero__visual">
            <div className="nx-hero__card nx-hero__card--1">Cloud</div>
            <div className="nx-hero__card nx-hero__card--2">Data</div>
            <div className="nx-hero__card nx-hero__card--3">DX</div>
          </div>
        </div>
        <div className="nx-wrap nx-stats">
          {STATS.map((s) => (
            <div key={s.label} className="nx-stat">
              <strong>{s.value}<small>{s.unit}</small></strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section id="services" className="nx-section">
        <div className="nx-wrap">
          <SiteReveal><div className="nx-head"><span className="nx-eyebrow">Services</span><h2>핵심 서비스</h2></div></SiteReveal>
          <div className="nx-grid4">
            {SERVICES.map((s) => (
              <SiteReveal key={s.title}>
                <article className="nx-card">
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  <ul>{s.items.map((i) => <li key={i}>{i}</li>)}</ul>
                </article>
              </SiteReveal>
            ))}
          </div>
        </div>
      </section>

      <section id="about" className="nx-section nx-section--alt">
        <div className="nx-wrap">
          <SiteReveal>
            <div className="nx-head"><span className="nx-eyebrow">Process</span><h2>프로젝트 진행 방식</h2></div>
          </SiteReveal>
          <div className="nx-steps">
            {PROCESS.map((p) => (
              <SiteReveal key={p.step}>
                <div className="nx-step"><span>{p.step}</span><h4>{p.title}</h4><p>{p.desc}</p></div>
              </SiteReveal>
            ))}
          </div>
        </div>
      </section>

      <section id="cases" className="nx-section">
        <div className="nx-wrap">
          <SiteReveal><div className="nx-head"><span className="nx-eyebrow">Cases</span><h2>고객 사례</h2></div></SiteReveal>
          <div className="nx-cases">
            {CASES.map((c) => (
              <SiteReveal key={c.client}>
                <article className="nx-case">
                  <span className="nx-case__tag">{c.industry}</span>
                  <h3>{c.client}</h3>
                  <p>{c.result}</p>
                </article>
              </SiteReveal>
            ))}
          </div>
          <SiteReveal>
            <div className="nx-clients">
              <span>주요 고객사</span>
              <div>{CLIENTS.map((c) => <span key={c}>{c}</span>)}</div>
            </div>
          </SiteReveal>
        </div>
      </section>

      <section id="team" className="nx-section nx-section--alt">
        <div className="nx-wrap">
          <SiteReveal><div className="nx-head"><span className="nx-eyebrow">Team</span><h2>핵심 인력</h2></div></SiteReveal>
          <div className="nx-grid4">
            {TEAM.map((t) => (
              <SiteReveal key={t.name}>
                <article className="nx-card nx-team">
                  <div className="nx-team__av">{t.name[0]}</div>
                  <h4>{t.name}</h4>
                  <span className="nx-team__role">{t.role}</span>
                  <p>{t.focus}</p>
                </article>
              </SiteReveal>
            ))}
          </div>
        </div>
      </section>

      <section id="contact" className="nx-section">
        <div className="nx-wrap nx-contact">
          <SiteReveal>
            <div>
              <div className="nx-head nx-head--left"><span className="nx-eyebrow">Contact</span><h2>프로젝트 문의</h2>
              <p>과제·예산·일정을 남겨 주시면 1영업일 내 담당자가 연락드립니다.</p></div>
              <p className="nx-contact__info">{BRAND.address}<br />{BRAND.phone} · {BRAND.email}</p>
            </div>
          </SiteReveal>
          <SiteReveal>
            <form className="nx-form" onSubmit={submit}>
              <div className="nx-form-row">
                <div><label>회사명</label><input required value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
                <div><label>담당자명</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              </div>
              <div><label>이메일</label><input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div>
                <label>문의 유형</label>
                <select required value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="">선택</option>
                  <option value="dx">DX 컨설팅</option>
                  <option value="cloud">클라우드</option>
                  <option value="data">데이터·AI</option>
                  <option value="dev">개발</option>
                </select>
              </div>
              <div><label>문의 내용</label><textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
              <button type="submit" className="nx-btn nx-btn--primary">문의 접수 <ArrowRight size={16} /></button>
            </form>
          </SiteReveal>
        </div>
      </section>

      <footer className="nx-footer">
        <div className="nx-wrap">
          <strong>{BRAND.name}</strong>
          <p>{BRAND.slogan}</p>
          <span>© 2026 {BRAND.nameEn}. Portfolio by DSiteSolution</span>
        </div>
      </footer>
      {modal && <Modal message="담당 컨설턴트가 확인 후 연락드리겠습니다." onClose={() => setModal(false)} />}
    </div>
  );
}
